import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contentPOsAPI } from '../services/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Receipt, DollarSign, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { POExtractionPanel } from '../components/purchase-orders/POExtractionPanel';
import { POLineItemsEditor } from '../components/purchase-orders/POLineItemsEditor';

const PO_STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  exhausted: 'bg-red-100 text-red-700',
  closed: 'bg-gray-100 text-gray-600',
};

const emptyForm = {
  po_number: '', short_title: '', total_amount: '', issue_date: new Date().toISOString().split('T')[0], expiry_date: '', description: '',
  ms_owner: '', line_items: [], document_path: '', document_filename: '', document_content_type: '',
};

export const ContentPOsPage = () => {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [viewPO, setViewPO] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await contentPOsAPI.getAll();
      setPos(data);
    } catch { toast.error('Failed to load Content POs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.po_number || !form.total_amount || !form.issue_date) {
      toast.error('Please fill in PO number, amount and issue date'); return;
    }
    setSaving(true);
    try {
      await contentPOsAPI.create({
        po_number: form.po_number,
        short_title: form.short_title || null,
        total_amount: parseFloat(form.total_amount),
        issue_date: form.issue_date,
        expiry_date: form.expiry_date || null,
        description: form.description || null,
        ms_owner: form.ms_owner || null,
        line_items: (form.line_items || [])
          .filter(li => li.description)
          .map(li => ({
            description: li.description,
            delivery_date: li.delivery_date || null,
            amount: parseFloat(li.amount) || 0,
          })),
        document_path: form.document_path || null,
        document_filename: form.document_filename || null,
        document_content_type: form.document_content_type || null,
      });
      toast.success('Content PO created');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create Content PO');
    } finally { setSaving(false); }
  };

  const handleExtracted = (result) => {
    const ex = result.extracted || {};
    setForm(prev => ({
      ...prev,
      po_number: ex.po_number || prev.po_number,
      short_title: ex.short_title || prev.short_title,
      total_amount: ex.total_amount != null ? String(ex.total_amount) : prev.total_amount,
      issue_date: ex.issue_date || prev.issue_date,
      expiry_date: ex.expiry_date || prev.expiry_date,
      description: ex.description || prev.description,
      ms_owner: ex.ms_owner || prev.ms_owner,
      line_items: Array.isArray(ex.line_items) && ex.line_items.length > 0
        ? ex.line_items.map(li => ({
            description: li.description || '',
            delivery_date: li.delivery_date || '',
            amount: li.amount != null ? String(li.amount) : '',
          }))
        : prev.line_items,
      document_path: result.document_path || prev.document_path,
      document_filename: result.document_filename || prev.document_filename,
      document_content_type: result.document_content_type || prev.document_content_type,
    }));
  };

  const openView = async (po) => {
    try {
      const detail = await contentPOsAPI.getById(po.id);
      setViewPO(detail);
    } catch { toast.error('Failed to load PO details'); }
  };

  const exhaustedCount = pos.filter(p => p.status === 'exhausted').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-pos-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Content POs</h1>
          <p className="text-muted-foreground">Microsoft Purchase Orders funding content build, refresh &amp; new-SKU activities</p>
        </div>
        <Button onClick={openCreate} data-testid="new-content-po-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
          <Plus className="w-4 h-4 mr-2" />New Content PO
        </Button>
      </div>

      {exhaustedCount > 0 && (
        <Card className="border-red-400 bg-red-50" data-testid="content-po-exhausted-alert">
          <CardContent className="pt-5">
            <p className="flex items-center gap-2 text-red-900 font-medium">
              <AlertTriangle className="w-4 h-4" />{exhaustedCount} Content PO{exhaustedCount > 1 ? 's' : ''} fully utilized — new build activities need a fresh/updated PO
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-5">
          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded" />)}</div>
          ) : pos.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Used</TableHead>
                  <TableHead>Utilization</TableHead>
                  <TableHead>Linked Items</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pos.map(po => (
                  <TableRow key={po.id} className="cursor-pointer hover:bg-muted/50" data-testid={`content-po-row-${po.id}`} onClick={() => openView(po)}>
                    <TableCell className="font-medium text-sm">
                      {po.po_number}
                      {po.short_title && <div className="text-xs text-muted-foreground font-normal">{po.short_title}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{po.description || '—'}</TableCell>
                    <TableCell className="text-sm"><span className="flex items-center gap-1"><DollarSign className="w-3 h-3 text-muted-foreground" />{po.total_amount.toLocaleString()}</span></TableCell>
                    <TableCell className="text-sm">${po.used_amount.toLocaleString()}</TableCell>
                    <TableCell className="w-32"><Progress value={po.utilization_percentage} className="h-2" /><span className="text-xs text-muted-foreground">{po.utilization_percentage}%</span></TableCell>
                    <TableCell className="text-sm text-center">{po.linked_items_count}</TableCell>
                    <TableCell><Badge className={PO_STATUS_STYLES[po.status]}>{po.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Content POs yet</h3>
              <p className="text-muted-foreground mb-4">Add the Microsoft PO that funds your content build activities</p>
              <Button onClick={openCreate} className="bg-lime text-lime-foreground hover:bg-lime/90"><Plus className="w-4 h-4 mr-2" />New Content PO</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="content-po-form-dialog">
          <DialogHeader>
            <DialogTitle>New Content PO</DialogTitle>
            <DialogDescription>Microsoft-issued PO funding content build/refresh activities</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <POExtractionPanel poType="content" onExtracted={handleExtracted} initialFilename={form.document_filename} />
            <div>
              <Label>PO Number</Label>
              <Input value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })} placeholder="e.g. MS-CONTENT-2026-001" data-testid="content-po-number-input" />
            </div>
            <div>
              <Label>Short Title</Label>
              <Input value={form.short_title} onChange={e => setForm({ ...form, short_title: e.target.value })} placeholder="e.g. Fabric FY26 Build" data-testid="content-po-short-title-input" />
            </div>
            <div>
              <Label>Total Amount ($)</Label>
              <Input type="number" value={form.total_amount} onChange={e => setForm({ ...form, total_amount: e.target.value })} data-testid="content-po-amount-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Issue Date</Label>
                <Input type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} data-testid="content-po-issue-date-input" />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} data-testid="content-po-expiry-date-input" />
              </div>
            </div>
            <div>
              <Label>Microsoft Owner</Label>
              <Input value={form.ms_owner} onChange={e => setForm({ ...form, ms_owner: e.target.value })} placeholder="e.g. Jane Doe (Microsoft)" data-testid="content-po-ms-owner-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Fabric content build FY26" data-testid="content-po-description-input" />
            </div>
            <POLineItemsEditor lineItems={form.line_items} onChange={(items) => setForm({ ...form, line_items: items })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="content-po-save-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">{saving ? 'Creating...' : 'Create Content PO'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewPO} onOpenChange={(o) => !o && setViewPO(null)}>
        <DialogContent className="max-w-2xl" data-testid="content-po-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewPO?.po_number}</DialogTitle>
            <DialogDescription>{viewPO?.description}</DialogDescription>
          </DialogHeader>
          {viewPO && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><p className="text-muted-foreground">Total</p><p className="font-medium">${viewPO.total_amount.toLocaleString()}</p></div>
                <div><p className="text-muted-foreground">Used</p><p className="font-medium">${viewPO.used_amount.toLocaleString()}</p></div>
                <div><p className="text-muted-foreground">Remaining</p><p className="font-medium">${viewPO.available_balance.toLocaleString()}</p></div>
              </div>
              {viewPO.ms_owner && (
                <div className="text-sm"><p className="text-muted-foreground">Microsoft Owner</p><p className="font-medium">{viewPO.ms_owner}</p></div>
              )}
              {viewPO.line_items?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Planned Line Items ({viewPO.line_items.length})</p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="content-po-view-line-items">
                    {viewPO.line_items.map(li => (
                      <div key={li.id} className="flex items-center justify-between text-sm border rounded-lg px-3 py-1.5">
                        <span className="truncate">{li.description}{li.delivery_date ? ` — ${li.delivery_date}` : ''}</span>
                        <span className="text-muted-foreground flex-shrink-0">${(li.amount || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm font-medium mb-2">Linked Content Items ({viewPO.linked_items?.length || 0})</p>
                {viewPO.linked_items?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {viewPO.linked_items.map(item => (
                      <button key={item.id} onClick={() => { setViewPO(null); navigate(`/content-items/${item.id}`); }}
                        className="w-full flex items-center justify-between text-sm border rounded-lg px-3 py-2 hover:bg-muted/50 transition-colors" data-testid={`po-linked-item-${item.id}`}>
                        <span className="truncate">{item.title}</span>
                        <span className="flex items-center gap-2 text-muted-foreground flex-shrink-0">
                          ${item.po_amount?.toLocaleString() || 0}<ArrowUpRight className="w-3.5 h-3.5" />
                        </span>
                      </button>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No content items linked yet</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentPOsPage;
